<template>
    <ClientOnly>
        <VJoyFormGroup>
            <VueTelInput
                :key="defaultCountryCode"
                :model-value="modelValue"
                v-bind="$attrs"
                :input-options="{placeholder: $t('generic.phone.placeholder')}"
                :preferred-countries="preferredCountries"
                :default-country="defaultCountryCode"
                :class="['vue-tel', {'vue-tel--isFocused': isFocused, 'vue-tel--invalid': resolvedField.$error}]"
                mode="international"
                :data-testid="dataTestid"
                @focus="isFocused = true"
                @blur="isFocused = false"
                @on-input="handlePhoneNumberInput"
            />
            <VJoyFormError
                v-if="resolvedField.$error && resolvedField.$errors.length > 0"
                :errors="resolvedField.$errors"
                class="vue-tel__errors"
                data-testid="phone-number-errors"
            />
        </VJoyFormGroup>
        <template v-if="props.withSkeleton" #fallback>
            <VJoyFormFieldSkeleton size="medium" />
        </template>
    </ClientOnly>
</template>
<script setup lang="ts">
    import {computed, onErrorCaptured, ref} from 'vue';
    import {useRegle, type MaybeInput, type RegleFieldStatus, type RegleRuleDecl} from '@regle/core';
    import {isFilled, requiredIf, withMessage, withParams} from '@regle/rules';
    /** @ts-expect-error VueTelInput is typed weirdly, the export is named but the type is for default export */
    import DefaultVueTelInput, {VueTelInput as VueTelInputV9, type PhoneMeta} from 'vue-tel-input-v9';
    import {useAsyncData, useLogger, useTranslation, useUniversalFetch} from '#imports';
    import {VJoyFormError, VJoyFormFieldSkeleton, VJoyFormGroup} from '@maltjoy/core-vue';
    import 'vue-tel-input/vue-tel-input.css';
    import type {PhoneSettings} from './PhoneNumber.types';

    /** Workaround for wrong types of vue-tel-input */
    const VueTelInput = VueTelInputV9 as typeof DefaultVueTelInput;

    const props = withDefaults(
        defineProps<{
            /**
             * Whether the phone number should be required (by Regle)
             */
            required?: boolean;
            /**
             * Whether the phone number should be lazy validated (by Regle)
             */
            lazyValidation?: boolean;
            /**
             * Whether the phone number should be displayed with a skeleton
             */
            withSkeleton?: boolean;
            /**
             * The country code to use for the phone number
             */
            countryCode?: string;
            /**
             * The Regle field to use for the phone number
             */
            field?: RegleFieldStatus<string | undefined>;
            /**
             * The data-testid to use for the phone number input
             */
            dataTestid?: string;
        }>(),
        {
            required: false,
            lazyValidation: false,
            withSkeleton: true,
            countryCode: '',
        },
    );

    const modelValue = defineModel<string>({default: ''});

    const logger = useLogger();
    const {t} = useTranslation();

    // --- Phone settings ---

    const defaultCountryCode = ref('fr');
    const preferredCountries = ref(['fr', 'gb', 'be', 'ch', 'lu', 'de', 'es', 'it']);

    onErrorCaptured((error) => {
        // Prevent Nuxt to display an error page (until https://github.com/iamstevendao/vue-tel-input/issues/460 is fixed)
        if (error.stack && /Proxy.keyboardNav/.test(error.stack as string)) {
            return false;
        }
    });

    const isFocused = ref(false);

    // --- Phone validation ---

    const isPhoneNumberValid = ref(false);

    /** Handle input event from the VueTelInput component */
    function handlePhoneNumberInput(value: string, phoneObject: PhoneMeta) {
        modelValue.value = phoneObject.formatted ?? value;
        isPhoneNumberValid.value = phoneObject.valid ?? false;
    }

    const rules = {
        requiredIf: withMessage(
            requiredIf(() => props.required),
            t('generic.phone.required'),
        ),
        validPhoneNumber: withParams(
            withMessage((value: MaybeInput<string>, isPhoneValid: boolean) => {
                if (!isFilled(value)) {
                    return true;
                }
                return isPhoneValid;
            }, t('generic.phone.incorrect')),
            [isPhoneNumberValid],
        ),
    } satisfies RegleRuleDecl<string>;

    // If a regle field is provided, add the phone validation rules.
    if (props.field) {
        props.field.$addRules(rules);
    }

    const {r$: fallbackR$} = useRegle(modelValue, rules, {lazy: () => props.lazyValidation, disabled: () => !!props.field});

    /**
     * Resolve the field to use for the phone number.
     * If a regle field is provided, use it, otherwise use the fallback field.
     */
    const resolvedField = computed(() => {
        if (props.field) {
            return props.field;
        }
        return fallbackR$;
    });

    defineExpose({
        r$: fallbackR$,
    });

    const {data: settings, error} = await useAsyncData('phone-settings', () => {
        const params = props.countryCode ? {countryCode: props.countryCode} : {};
        return useUniversalFetch<PhoneSettings>('/api/component/phone', {params});
    });

    if (error.value) {
        logger.error(error.value);
    } else if (settings.value) {
        defaultCountryCode.value = settings.value.defaultCountryCode;
        preferredCountries.value = settings.value.lowerCasedSuggestedCountriesForPhoneNumbers;
    }
</script>

<style scoped lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .vue-tel {
        position: relative;
        border-radius: var(--joy-form-field-radius-medium);
        height: var(--joy-form-field-height-medium);
        border-color: var(--joy-color-neutral-30);
        border-width: var(--joy-form-field-border-width);
        background-color: var(--joy-color-background-default);
        transition: all var(--joy-transition-duration-default) ease-in-out;

        &:hover,
        &--isFocused,
        &:focus-within {
            border-color: var(--joy-color-border-focus);
            box-shadow: none;
        }

        &--invalid {
            border-color: var(--joy-color-error-50);
        }

        &__errors {
            margin-top: var(--joy-core-spacing-2);

            &__item {
                margin-bottom: 0 !important;
            }
        }
    }

    :deep(.vti__input) {
        background-color: transparent;
        border-left: 2px solid var(--joy-color-neutral-30);
    }

    :deep(.vti__dropdown) {
        position: static;
        padding: var(--joy-core-spacing-3) var(--joy-core-spacing-1) var(--joy-core-spacing-3) var(--joy-core-spacing-3);
        border-radius: var(--joy-form-field-radius-medium) 0 0 var(--joy-form-field-radius-medium);
        border: 3px solid var(--joy-color-neutral-0);

        &:hover {
            background-color: var(--joy-color-secondary-10);
        }
    }

    :deep(.vti__dropdown-list) {
        width: 100%;
        margin-top: var(--joy-core-spacing-1);
        z-index: var(--joy-core-z-index-dropdown);
        border-radius: var(--joy-core-radius-3);
        transform: translateY(var(--joy-core-spacing-4));
        border: var(--joy-form-field-border-width) solid var(--joy-color-secondary-30);

        li {
            min-height: var(--joy-form-field-height-medium);
            display: flex;
            align-items: center;
            gap: var(--joy-core-spacing-2);

            &:hover,
            &.highlighted {
                background-color: var(--joy-color-secondary-10);
            }
        }
    }
</style>
